import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiGet } from "@/lib/api";

interface DocSeq {
  docType: string;
  mode: "auto" | "manual";
  prefix: string;
  padding: number;
  includeYear: boolean;
  perProject: boolean;
}

interface RefNumberInputProps {
  docType: string;
  companyId: string;
  projectId?: string;
  editingValue?: string;
  label?: string;
  required?: boolean;
  /** When false (docSequencing feature not enabled), always render a plain manual input. */
  enabled?: boolean;
}

export function RefNumberInput({
  docType,
  companyId,
  projectId,
  editingValue,
  label = "Reference No.",
  required,
  enabled = true,
}: RefNumberInputProps) {
  const [seq, setSeq] = useState<DocSeq | null>(null);
  const [autoRef, setAutoRef] = useState<string>("");
  const [override, setOverride] = useState(false);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setAutoRef("");

    const params = new URLSearchParams({ companyId });
    apiGet<DocSeq[]>(`/doc-sequences?${params}`)
      .then(seqs => {
        const s = seqs.find(x => x.docType === docType);
        if (!s) return undefined;
        setSeq(s);

        if (s.mode === "auto" && !editingValue) {
          const q = new URLSearchParams({ companyId });
          if (projectId) q.set("projectId", projectId);
          return apiGet<{ refNumber: string | null }>(`/doc-sequences/${docType}/next-ref?${q}`);
        }
        return undefined;
      })
      .then(r => {
        if (r?.refNumber) setAutoRef(r.refNumber);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [docType, companyId, projectId, editingValue, enabled]);

  if (loading) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm">{label}</Label>
        <div className="h-10 flex items-center px-3 border border-border/40 rounded-xl bg-muted/20">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  const isManual = !enabled || !seq || seq.mode === "manual" || override;

  if (isManual) {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-sm">{label}</Label>
          {enabled && seq?.mode === "auto" && override && (
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={() => setOverride(false)}
            >
              ← Use auto
            </button>
          )}
        </div>
        <Input
          name="refNumber"
          defaultValue={editingValue ?? ""}
          placeholder={enabled && seq?.mode === "manual" ? "Enter reference number" : (editingValue || autoRef || "e.g. INV-001")}
          className="rounded-xl font-mono text-sm"
          required={required}
        />
      </div>
    );
  }

  const displayRef = editingValue || autoRef;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm">{label}</Label>
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => setOverride(true)}
        >
          Override
        </button>
      </div>
      <div className="h-10 flex items-center justify-between px-3 rounded-xl border border-border/50 bg-muted/30">
        <span className="font-mono text-sm font-semibold text-foreground">
          {displayRef || <span className="text-muted-foreground italic text-xs">Auto-generating…</span>}
        </span>
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 bg-muted px-1.5 py-0.5 rounded">Auto</span>
      </div>
      <input type="hidden" name="refNumber" value={displayRef} />
    </div>
  );
}
